import React, { lazy, Suspense } from 'react';
import { useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';
import { useMaintenanceCheck } from '@/hooks/useMaintenanceCheck';
import { isWhitelistedAdmin } from '@/utils/adminWhitelist';
import { useLanguage } from '@/contexts/LanguageContext';

// Lazy load maintenance page - only loads when actually needed
const MaintenancePage = lazy(() => import('./MaintenancePage'));

interface MaintenanceGuardProps {
  children: React.ReactNode;
}

export default function MaintenanceGuard({ children }: MaintenanceGuardProps) {
  const location = useLocation();
  const { language } = useLanguage();
  const { isEnabled, settings, isLoading: maintenanceLoading } = useMaintenanceCheck();
  
  // Skip admin check on auth page
  const isAuthPage = location.pathname === '/auth';
  const { user, isAdmin, isLoading: authLoading } = useSupabaseAuth(isAuthPage);
  
  const effectiveIsAdmin = isAdmin || (user?.email ? isWhitelistedAdmin(user.email) : false);

  // Helper to pick translation with proper fallback
  const pickTranslation = (
    base: string,
    translations?: Record<string, string>,
    lang?: string
  ) => {
    const v = translations?.[lang ?? ''];
    if (typeof v !== 'string') return base;
    const cleaned = v.trim();
    if (!cleaned || /INVALID SOURCE LANGUAGE/i.test(cleaned)) return base;
    return cleaned;
  };

  // Render children immediately while loading (no flash)
  if (maintenanceLoading || authLoading) {
    return <>{children}</>;
  }

  // If maintenance is disabled, render children immediately (no extra logic)
  if (!isEnabled) {
    return <>{children}</>;
  }

  // Maintenance is enabled - check admin status
  if (effectiveIsAdmin) {
    return <>{children}</>;
  }

  // Show maintenance page for non-admin users
  return (
    <Suspense fallback={<>{children}</>}>
      <MaintenancePage
        mainMessage={pickTranslation(settings?.main_message || 'Site under maintenance', settings?.main_message_translations, language)}
        additionalMessage={pickTranslation(settings?.additional_message || '', settings?.additional_message_translations, language)}
      />
    </Suspense>
  );
}
